import type { ReactNode } from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';

import { CircularProgressBarScore } from '../CircularProgressBarScore';
import { ProgressBarScore } from '../ProgressBarScore';

export type ScoreSummaryProps = {
  scoreSummary: {
    accreditationLogo?: ReactNode;
    score?: number;
    scoreSupportingText?: string;
  };
  scoreSections: {
    name: string;
    score: number;
    resultType?: 'percentage' | 'boolean';
  }[];
  reviewResultType?: 'percentage' | 'boolean';
};

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(4)} 0`,
  display: 'grid',
  gap: theme.spacing(2),
}));

/**
 * ScoreSummary component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {ScoreSummaryProps} scoreSummaryProps - The props for the ScoreSummary component
 * @returns {ReactElement} The ScoreSummary component
 */

export const ScoreSummary = ({
  scoreSummary,
  scoreSections,
  reviewResultType,
}: ScoreSummaryProps) => {
  const {
    score: overallScore,
    scoreSupportingText,
    accreditationLogo,
  } = scoreSummary;
  const theme = useTheme();

  const hasThreeColumns = accreditationLogo && overallScore;
  const hasMinTwoColumns = accreditationLogo || overallScore;

  const columnLayout = () => {
    if (hasThreeColumns) {
      return '80px 80px 1fr';
    }

    if (hasMinTwoColumns) {
      return '80px 1fr';
    }

    return '1fr';
  };

  const resultType = reviewResultType === 'boolean' ? 'boolean' : 'percentage';

  return (
    <Container>
      <Box
        sx={{
          display: 'grid',
          gap: theme.spacing(4),
          gridTemplateColumns: columnLayout(),
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
          },
        }}
      >
        {accreditationLogo && (
          <Box
            height="76px"
            width="76px"
            sx={{ margin: 'auto', overflow: 'hidden' }}
          >
            {accreditationLogo}
          </Box>
        )}

        {resultType === 'percentage' && overallScore && (
          <Box height="76px" width="76px" sx={{ margin: 'auto' }}>
            <CircularProgressBarScore value={Math.round(overallScore)} />
          </Box>
        )}
        {resultType === 'boolean' && (
          <Box
            height="76px"
            width="76px"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: overallScore! === 0 ? 'error.main' : 'success.main',
            }}
          >
            <Typography variant="h4">
              {overallScore! === 0 ? 'FAIL' : 'PASS'}
            </Typography>
          </Box>
        )}
        {scoreSupportingText && <Typography>{scoreSupportingText}</Typography>}
      </Box>
      {scoreSections?.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: theme.spacing(2),
          }}
        >
          {scoreSections?.map((section) => {
            const { name, score: sectionScore } = section;
            return (
              <Box key={name}>
                <ProgressBarScore
                  label={name}
                  score={Math.round(sectionScore)}
                  resultType={section.resultType ?? resultType}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Container>
  );
};
